import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, ArrowRight, RotateCcw, Trophy, Home } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { showRewards } from "@/lib/rewards";
import Icon from "@/components/Icon";
import { STAT_META } from "@/lib/colors";
import { Button } from "@/components/ui/button";

const clampStat = (key, v) => {
  if (key === "money") return Math.max(0, Math.round(v));
  if (key === "credit") return Math.max(300, Math.min(850, Math.round(v)));
  return Math.max(0, Math.min(100, Math.round(v)));
};

const fmt = (key, v) => (key === "money" ? `$${v.toLocaleString()}` : v);

const RATING_COLORS = { S: "from-yellow-400 to-amber-500", A: "from-emerald-500 to-teal-400", B: "from-cyan-500 to-blue-500", C: "from-orange-500 to-amber-500", D: "from-rose-500 to-pink-500" };

export default function SimulationPlayer() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [sim, setSim] = useState(null);
  const [nodeId, setNodeId] = useState(null);
  const [stats, setStats] = useState({});
  const [initial, setInitial] = useState({});
  const [deltas, setDeltas] = useState({});
  const [path, setPath] = useState([]);
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    api.get(`/simulations/${key}`).then((r) => {
      if (r.data.locked) { navigate("/app/premium"); return; }
      setSim(r.data);
      setNodeId(r.data.start);
      setStats(r.data.initial_stats);
      setInitial(r.data.initial_stats);
    }).catch(() => navigate(-1));
  }, [key]);

  const node = sim?.nodes?.[nodeId];
  const isEnding = node?.ending;

  useEffect(() => {
    if (isEnding && sim && !rewards) {
      api.post("/simulations/complete", { simulation_id: key, ending_key: node.key, final_stats: stats, path })
        .then((r) => { setRewards(r.data); showRewards(r.data); refreshUser(); })
        .catch(() => {});
    }
  }, [isEnding]);

  const choose = (choice) => {
    const newStats = { ...stats };
    const newDeltas = {};
    Object.entries(choice.effects || {}).forEach(([k, v]) => {
      if (v) { newStats[k] = clampStat(k, (newStats[k] || 0) + v); newDeltas[k] = v; }
    });
    setStats(newStats);
    setDeltas(newDeltas);
    setPath([...path, nodeId]);
    setTimeout(() => setDeltas({}), 1400);
    setNodeId(choice.next);
  };

  const restart = () => { setNodeId(sim.start); setStats(sim.initial_stats); setInitial(sim.initial_stats); setDeltas({}); setPath([]); setRewards(null); };

  if (!sim || !node) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  const keyStats = ["money", "stress", "happiness", "career", "health", "relationships", "credit", "reputation"];

  return (
    <div className="min-h-screen aurora bg-background text-foreground">
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="font-display font-bold text-sm truncate">{sim.title}</span>
          <button data-testid="close-sim-btn" onClick={() => navigate(-1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/5"><X size={20} /></button>
        </div>
        {/* Stat rail */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
          {keyStats.map((k) => {
            const meta = STAT_META[k];
            const d = deltas[k];
            return (
              <div key={k} data-testid={`stat-${k}`} className="relative shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <Icon name={meta.icon} size={13} className={meta.color} />
                <span className={`text-xs font-bold ${meta.color}`}>{fmt(k, stats[k] || 0)}</span>
                <AnimatePresence>
                  {d ? (
                    <motion.span initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: -14 }} exit={{ opacity: 0 }}
                      className={`absolute left-1/2 -translate-x-1/2 -top-1 text-[11px] font-extrabold ${d > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {d > 0 ? "+" : ""}{k === "money" ? `$${Math.abs(d)}`.replace("$-", "-$") : d}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-16">
        <AnimatePresence mode="wait">
          {isEnding ? (
            <motion.div key="ending" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
              <div className={`inline-grid place-items-center h-20 w-20 rounded-3xl bg-gradient-to-br ${RATING_COLORS[node.rating] || RATING_COLORS.B} shadow-2xl mb-5 font-display font-black text-4xl text-white`}>{node.rating}</div>
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Ending Unlocked</span>
              <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1 mb-4">{node.title}</h1>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-8">{node.text}</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-8">
                {keyStats.map((k) => {
                  const meta = STAT_META[k];
                  const diff = (stats[k] || 0) - (initial[k] || 0);
                  return (
                    <div key={k} className="glass rounded-2xl p-3">
                      <Icon name={meta.icon} size={16} className={`${meta.color} mx-auto mb-1`} />
                      <div className="text-sm font-display font-bold">{fmt(k, stats[k] || 0)}</div>
                      <div className={`text-[10px] font-bold ${diff > 0 ? "text-emerald-400" : diff < 0 ? "text-rose-400" : "text-muted-foreground"}`}>{diff > 0 ? "▲" : diff < 0 ? "▼" : "–"} {meta.label}</div>
                    </div>
                  );
                })}
              </div>

              {rewards && (
                <div className="flex justify-center gap-3 mb-8">
                  <div className="glass rounded-2xl px-5 py-3"><div className="text-xl font-display font-extrabold text-yellow-400">+{rewards.xp_gained}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">XP</div></div>
                  <div className="glass rounded-2xl px-5 py-3"><div className="text-xl font-display font-extrabold text-emerald-400">+{rewards.coins_gained}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Coins</div></div>
                </div>
              )}

              <div className="flex gap-3 max-w-md mx-auto">
                <Button data-testid="sim-replay-btn" onClick={restart} variant="outline" className="flex-1 py-3 rounded-2xl bg-white/5 border-white/10 font-semibold"><RotateCcw size={16} className="mr-1.5" /> Replay</Button>
                <Button data-testid="sim-exit-btn" onClick={() => navigate("/app")} className="flex-1 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"><Home size={16} className="mr-1.5" /> Home</Button>
              </div>
            </motion.div>
          ) : (
            <motion.div key={nodeId} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
              <div className="glass rounded-3xl p-6 mb-6 border border-indigo-500/15">
                <span className="text-xs uppercase tracking-widest font-bold text-indigo-400">Scenario</span>
                <p className="text-lg leading-relaxed mt-2">{node.text}</p>
              </div>
              <p className="text-sm text-muted-foreground font-semibold mb-3 uppercase tracking-wider">What do you do?</p>
              <div className="space-y-3">
                {node.choices.map((c, i) => (
                  <motion.button key={i} data-testid={`choice-${i}`} whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }} onClick={() => choose(c)}
                    className="w-full text-left flex items-center justify-between gap-3 glass rounded-2xl p-5 border border-white/10 hover:border-indigo-500/40 transition-colors">
                    <span className="font-semibold text-[15px]">{c.label}</span>
                    <ArrowRight size={18} className="text-indigo-400 shrink-0" />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
